import React, { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";

import { AppContext } from "../../../../core/context/appContextProvider";
import { setDocControlPermissions } from "../../../../services/documents.service";
import { FolderContentRequestPayload } from "../../../../utils/types/documents.type";
import { DocumentsState } from "../../types/hook.types";

type Tags = {
  downloadEnabled?: string;
  printEnabled?: string;
  copyEnabled?: string;
};

type File = {
  id: string;
  type: "folder" | "file";
  fileType: string;
};

const docControlOptions = [
  {
    id: "keepExisting",
    label: "Keep Existing",
  },
  {
    id: "on",
    label: "On",
  },
  {
    id: "off",
    label: "Off",
  },
];

const useEditPanel = (state: DocumentsState | null, props: any) => {
  const { informationAlert } = useContext(AppContext);
  const [loading, setLoading] = React.useState<boolean>(false);

  const pdfFiles = props.files.filter((file: File) => file.fileType === "pdf");
  const pdfFileIds = pdfFiles.map((file: File) => file.id);
  const pdfFileCount = pdfFileIds.length;

  useEffect(() => {
    onClose();
  }, [state?.cursor]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
    watch,
    setError,
  } = useForm<Tags>({
    defaultValues: {
      downloadEnabled: "keepExisting",
      printEnabled: "keepExisting",
      copyEnabled: "keepExisting",
    },
  });

  const resetForm = () => {
    setLoading(false);
    reset();
  };

  const handleSave = async (data: any) => {
    setLoading(true);

    const submitData: {
      [key: string]: boolean | string;
    } = {};

    for (const key in data) {
      const value = data[key];

      if (value === "on") {
        submitData[key] = true;
      } else if (value === "off") {
        submitData[key] = false;
      }
    }

    try {
      await setDocControlPermissions(pdfFileIds, submitData);
    } catch (error) {
      informationAlert("Error setting document permissions", "error");
    } finally {
      setLoading(false);

      if (state?.currentFolder?.id) {
        const filters = { ...(state?.selectedFilters || {}) };

        if (
          state.selectedFilters["funds"].length ===
          state.filtersData["funds"].length
        ) {
          filters.funds = [];
        }
        if (
          state.selectedFilters["investors"].length ===
          state.filtersData["investors"].length
        ) {
          filters.investors = [];
        }
        if (
          state.selectedFilters["quarters"].length ===
          state.filtersData["quarters"].length
        ) {
          filters.quarters = [];
        }

        const payload: FolderContentRequestPayload = {
          ...filters,
          ...state.pagination,
          sort: state.sort,
        };

        resetForm();
        props.handleClose();
        props.reload(state?.currentFolder?.id, payload);
      }
    }
  };

  const onClose = () => {
    if (loading) return;
    resetForm();
    props.handleClose();
  };

  return {
    loading,
    register,
    errors,
    control,
    handleSubmit,
    handleSave,
    docControlOptions,
    getValues,
    setValue,
    resetForm,
    reset,
    watch,
    onClose,
    pdfFileCount,
  };
};

export default useEditPanel;
